import { Show, SimpleShowLayout, FunctionField, useGetOne, Loading, usePermissions, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DeviceStateField } from '@/components/device/fields/DeviceStateField.jsx';
import { MoreOptionsPopover, DeviceIssueErrorField } from '@/pages/deviceIssue.jsx';
import DeviceCollectionPointField from '@/components/device/DeviceCollectionPointField.jsx';
import DeviceErrorReportList from '@/components/device/DeviceErrorReportList.jsx';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection';
import ServiceHistory from '@/components/service/ServiceHistory.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import DeviceLogs from '@/components/device/DeviceLogs.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import DeviceLogsHeader from '@/components/device/DeviceLogsHeader.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';

const StyledDeviceIssueShow = styled(Box)(() => ({
    '& .device-issue-drawer': {
        '.RaSimpleShowLayout-root': {
            padding: '0 !important',
        },
    },
    '& .RaShow-noActions': {
        margin: '0',
    },
    '& .popup-wrapper-top': {
        borderBottom: `2px solid ${Palette.gray.gray75}`,
        '& .popup-box--header': {
            padding: 0,
        },
    },
    '& .simple-show-layout': {
        padding: '0',
        position: 'relative',
        '& .popup-wrapper': {
            padding: '0 !important',
        },
    },
    '& .popup-box--header': {
        cursor: 'pointer',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '20px 20px',
        },
        alignItems: 'center',
        '& h4': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                fontSize: '22px',
            },
        },
        '& .MuiIconButton-root': {
            padding: '12px !important',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                padding: '8px !important',
            },
        },
    },
    '& .pos-field-name': {
        p: {
            margin: 0,
        },
    },
    '& .pos-field-name-id': {
        fontWeight: 600,
    },
    '& .pos-field-info': {
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            marginTop: '4px',
        },
        '& .pos-field-address': {
            fontSize: '14px',
            marginBottom: '4px',
        },
        '& .pos-field-phone': {
            fontSize: '14px',
        },
    },
    '& .ra-field.popup-wrapper': {
        padding: '0',
    },
    '& .ra-field': {
        '&.ra-field-serialNumber': {
            '& span.MuiTypography-body2': {
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px !important',
                fontWeight: '400 !important',
            },
        },
    },
    '& .MuiStack-root': {
        margin: 0,
    },
    '& .details-simple-show-layout': {
        margin: '0',
        '& .MuiStack-root.ra-field': {
            display: 'flex',
            flexDirection: 'row',
            borderTop: `2px solid ${Palette.gray.gray75}`,
            marginBottom: '-2px',
            padding: '20px 0',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
            '&.ra-field-serialNumber': {
                '& span.MuiTypography-body2': {
                    fontSize: '18px',
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontWeight: '700',
                },
            },
            '& span.MuiTypography-body2': {
                fontSize: '14px',
            },
        },
    },
    '& .MuiTypography-root.RaLabeled-label': {
        fontSize: '14px !important',
        fontWeight: '400',
        lineHeight: '24px',
        width: '120px',
        minWidth: '120px',
        color: Palette.gray.gray700,
        margin: '0 !important',
        paddingRight: '28px',
    },
    '& .device-state-field-name': {
        fontWeight: 600,
    },
    '& .error-field-container': {
        fontWeight: 600,
        marginBottom: '4px',
        '& .MuiChip-label': {
            fontSize: '14px',
        },
        '& .error-icon': {
            display: 'none',
        },
        '& .info-icon': {
            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                display: 'none',
            },
            display: 'flex',
            alignItems: 'center',
        },
    },
    '& .error-title-container': {
        marginBottom: '4px',
    },
    '& .MuiButtonBase-root.close-popup': {
        padding: '12px 16px !important',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '12px !important',
        },
    },
    '& .MuiTypography-h5': {
        fontSize: '22px',
    },
    '& .present-state': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiCard-root.scroll-container': {
        paddingBottom: '4px',
    },
    '& .MuiToolbar-root.MuiToolbar-gutters.MuiTablePagination-toolbar': {
        borderBottom: `2px solid ${Palette.gray.gray75}`,
        padding: '9px 0',
    },
    '& .device-error-field': {
        '& .error-time': {
            display: 'block !important',
        },
    },
    '& .chip-small': {
        height: '20px',
        borderRadius: '6px',
        '& .MuiChip-label': {
            padding: '0 6px',
        },
    },
    '& form': {
        padding: '0 0 30px !important',
    },
    '& .device-error-report-list-wrapper': {
        '& .MuiChip-root': {
            borderRadius: '8px',
            '& .MuiChip-label': {
                padding: '0 6px',
                fontSize: '14px',
            },
        },
    },
}));

export const DeviceIssueShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    const { data, isLoading } = useGetOne('device-issue', { id: elementId });
    const { permissions } = usePermissions();
    const [isSummaryOpen, setIsSummaryOpen] = useState(true);
    const [isErrorsOpen, setIsErrorsOpen] = useState(false);
    const [isServiceHistoryOpen, setIsServiceHistoryOpen] = useState(false);
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [logsRequested, setLogsRequested] = useState(false);

    const isClient = permissions.isClient || permissions.isClientMaster;

    if (isLoading) {
        return <Loading />;
    }

    return (
        <StyledDeviceIssueShow>
            <Box className='header'>
                <Typography variant='h4' component='h1'>
                    {data.title}
                </Typography>
            </Box>
            <Box>
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('resources.device.labels.problemDetails')}</Typography>
                        <MoreOptionsPopover record={data} buttonLabel={translate('common.options')} />
                        <Box>
                            <CustomCloseButton className='close-button' onClose={handleClose} />
                        </Box>
                    </Box>
                </Box>
                <Show id={elementId} actions={false}>
                    <SimpleShowLayout className='simple-show-layout'>
                        <CollapsibleSection
                            title={translate('campaign.steps.summary')}
                            isOpen={isSummaryOpen}
                            onToggle={() => setIsSummaryOpen(!isSummaryOpen)}
                        >
                            <SimpleShowLayout className='details-simple-show-layout'>
                                <FunctionField
                                    label={translate('common.collectionPoint')}
                                    sortBy='collectionPoint.customId'
                                    render={data => <DeviceCollectionPointField collectionPoint={data.collectionPoint} />}
                                />
                                <FunctionField
                                    label={translate('resources.serviceRequest.labels.serialNumberLabel')}
                                    source='serialNumber'
                                    render={record => <DrawerLink resource='device' action='show' source={record.id} label={record.serialNumber} relative />}
                                />
                                <FunctionField
                                    className='present-state'
                                    label={translate('resources.device.labels.currentState')}
                                    render={data => <DeviceStateField record={data.device} />}
                                />
                                {data.error && (
                                    <FunctionField
                                        label={translate('resources.serviceRequest.labels.causeTitle')}
                                        render={data => <DeviceIssueErrorField record={data} isUnified={true} />}
                                    />
                                )}
                            </SimpleShowLayout>
                        </CollapsibleSection>
                        <CollapsibleSection
                            title={translate('resources.device.labels.lastErrors')}
                            isOpen={isErrorsOpen}
                            onToggle={() => setIsErrorsOpen(!isErrorsOpen)}
                        >
                            <Box className='device-error-report-list-wrapper'>
                                <DeviceErrorReportList deviceId={elementId} shouldExtend={true} />
                            </Box>
                        </CollapsibleSection>
                        {!isClient && (
                            <CollapsibleSection
                                title={translate('resources.device.labels.logs')}
                                isOpen={isLogsOpen}
                                onToggle={() => setIsLogsOpen(!isLogsOpen)}
                                headerContent={<DeviceLogsHeader deviceId={elementId} logsRequested={logsRequested} />}
                            >
                                <DeviceLogs className='device-logs' deviceId={elementId} onLogsRequestedChange={setLogsRequested} />
                            </CollapsibleSection>
                        )}
                        <CollapsibleSection
                            title={translate('resources.device.labels.serviceHistory')}
                            isOpen={isServiceHistoryOpen}
                            onToggle={() => setIsServiceHistoryOpen(!isServiceHistoryOpen)}
                        >
                            <ServiceHistory deviceId={elementId} shouldExtend={true} />
                        </CollapsibleSection>
                    </SimpleShowLayout>
                </Show>
            </Box>
        </StyledDeviceIssueShow>
    );
};

DeviceIssueShow.propTypes = {
    elementId: PropTypes.string.isRequired,
};
